import { Context } from "chartjs-plugin-datalabels";
import { floatingLabels } from "../../utils/chartjsPlugins";
import { Paper } from "@mui/material";
import { experimentalStyled as styled } from "@mui/material/styles";
import { Chart, registerables } from 'chart.js';
import { Bar, Doughnut } from "react-chartjs-2";
import { Type1Question, Question } from "../../models/Question";
import { scoreInterpolationColor } from "../../utils/scoreColor";
import { useState } from "react";

// Registrar chart.js después de los imports
Chart.register(...registerables);

const Item = styled(Paper)(({ theme }) => ({
  ...theme.typography.body2,
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  color: theme.palette.text.primary,
  width: "100%",
  height: "",
  background: '#fff',
  borderRadius: '12px',
  border: '1.5px solid #e0e0e0',
  boxShadow: 'none',
}));

interface Props {
  index: number;
  question: Type1Question;
  templateId?: number;
}

export function Type1QuestionChart({ index, question, templateId }: Props): JSX.Element {
  const [tooltip, setTooltip] = useState<{ visible: boolean; text: string; x: number; y: number }>({
    visible: false,
    text: '',
    x: 0,
    y: 0
  });

  // Detectar si es radiogroup o checkbox
  const isBar = question.type === 'radiogroup' || question.type === 'checkbox';
  // Detectar si es ENPS y primera pregunta para usar barras verticales
  const isENPSFirstQuestion = templateId === 4 && index === 1;
  const isVerticalBar = isENPSFirstQuestion;
  // let chartConfig = new RatingChartConfig(question);
  // let { options, data } = chartConfig.createChartConfig();
  // options.plugins = { legend: { display: false } };
  // // options.maintainAspectRatio = false;

  const sortedChoices = question.choices
    .reduce(
      (
        sortedChoices: [string, number][],
        currentValue: string,
        currentIndex: number
      ) => {
        sortedChoices.push([currentValue, question.choiceValues[currentIndex]]);
        return sortedChoices;
      },
      [] as [string, number][]
    );
  
  // Solo ordenar si NO es la gráfica vertical de ENPS
  if (!isVerticalBar) {
    sortedChoices.sort(([, a], [, b]) => b - a);
  }

  /* Example:
  const data = {
    labels: ["Red", "Blue", "Yellow", "Green", "Purple", "Orange"],
    datasets: [
      {
        label: "# of Votes",
        data: [12, 19, 3, 5, 2, 3],
        borderWidth: 1,
      },
    ],
  };
  
  const options = {
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };
  */

  const colors = question.choices.map((value, index) =>
    scoreInterpolationColor(index, question.maxScore)
  );

  // Calcular el total para convertir a porcentajes
  const total = sortedChoices.reduce((sum, [, v]) => sum + v, 0);

  const data = {
    labels: sortedChoices.map((value) => value[0]),
    datasets: [
      {
        label: "label",
        data: sortedChoices.map((value) => total > 0 ? (value[1] / total) * 100 : 0),
        borderWidth: 1,
        backgroundColor: colors,
      },
    ],
  };

  const options = isBar ? {
    indexAxis: isVerticalBar ? 'x' as const : 'y' as const,
    layout: {
      padding: {
        right: 20
      }
    },
    onHover: (event: any, activeElements: any) => {
      if (activeElements.length > 0) {
        const dataIndex = activeElements[0].index;
        const fullLabel = sortedChoices[dataIndex][0];
        const value = sortedChoices[dataIndex][1];
        const total = sortedChoices.reduce((sum, [, v]) => sum + v, 0);
        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
        
        setTooltip({
          visible: true,
          text: `${fullLabel}: ${percentage}%`,
          x: event.native.clientX,
          y: event.native.clientY
        });
      } else {
        setTooltip({ visible: false, text: '', x: 0, y: 0 });
      }
    },
    plugins: {
      legend: {
        display: false
      },
      tooltip: {
        enabled: false
      },
      datalabels: {
        color: '#111',
        textAlign: 'center',
        font: { lineHeight: 1.6 },
        formatter: (value: number, ctx: Context) => {
          // Ahora todos los valores ya están en porcentajes
          return value > 0 ? value.toFixed(1) + '%' : '';
        },
      },
    },
    responsive: true,
    maintainAspectRatio: isVerticalBar ? false : true,
    scales: isVerticalBar ? {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: {
          stepSize: 20,
          callback: function(value: any) {
            return value + '%';
          }
        }
      },
      x: {
        ticks: {
          autoSkip: false,
          font: {
            size: 11
          }
        }
      }
    } : {
      x: {
        beginAtZero: true,
        max: 100,
        ticks: {
          stepSize: 20,
          callback: function(value: any) {
            return value + '%';
          }
        }
      },
      y: {
        ticks: {
          autoSkip: false,
          font: {
            size: 11
          },
          callback: (value: any, index: number): string => {
            const label = data.labels?.[index];
            if (label && typeof label === 'string' && label.length > 30) {
              return label.substring(0, 30) + '...';
            }
            return label ? String(label) : String(value);
          }
        }
      }
    }
  } : {
    onHover: (event: any, activeElements: any) => {
      if (activeElements.length > 0) {
        const dataIndex = activeElements[0].index;
        const fullLabel = sortedChoices[dataIndex][0];
        const value = sortedChoices[dataIndex][1];
        const total = sortedChoices.reduce((sum, [, v]) => sum + v, 0);
        const percentage = total > 0 ? ((value / total) * 100).toFixed(1) : '0.0';
        
        setTooltip({
          visible: true,
          text: `${fullLabel}: ${percentage}%`,
          x: event.native.clientX,
          y: event.native.clientY
        });
      } else {
        setTooltip({ visible: false, text: '', x: 0, y: 0 });
      }
    },
    plugins: {
      tooltip: {
        enabled: false
      },
      datalabels: {
        color: "#111",
        textAlign: "center",
        font: { lineHeight: 1.6 },
        formatter: (value: number, ctx: Context) => {
          if (ctx.chart.data.labels && value > 0) {
            return (
              (
                (value /
                  question.choiceValues.reduce(
                    (previousValue, currentValue) =>
                      previousValue + currentValue
                  )) *
                100
              ).toFixed(2) + "%"
            );
          } else if (value === 0) {
            return "";
          }
        },
      },
    },
    responsive: true,
    maintainAspectRatio: false
  };

  // console.log("choices", question.choices);
  // console.log("choiceValues", question.choiceValues);
  // console.log("colors", colors);

  return (
    <Item style={{ 
      height: isENPSFirstQuestion ? "420px" : "auto", 
      position: "relative",
      display: "flex",
      flexDirection: "column",
      alignItems: isENPSFirstQuestion ? "center" : "flex-start",
      paddingBottom: isENPSFirstQuestion ? "40px" : "24px"
    }}>
      <h3
        style={{
          fontSize: "18px",
          fontWeight: 600,
          color: "#1A237E",
          fontFamily: "'IBM Plex Sans', sans-serif",
          width: "100%",
          textAlign: "left",
          marginBottom: "40px"
        }}
      >
        {index}. {question.title.length > 100 ? question.title.substring(0, 100) + '...' : question.title}
      </h3>
      <div 
        style={{ 
          width: isENPSFirstQuestion ? "70%" : "100%", 
          maxWidth: isENPSFirstQuestion ? "800px" : "none",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center"
        }}
        onMouseLeave={() => setTooltip({ visible: false, text: '', x: 0, y: 0 })}
      >
        <div style={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
          {isBar ? (
            <Bar
              // @ts-ignore
              options={options}
              data={data}
            />
          ) : (
            <Doughnut
              // @ts-ignore
              options={options}
              data={data}
              // plugins={[floatingLabels]}
            />
          )}
        </div>
        
        {/* Etiquetas adicionales para ENPS */}
        {isENPSFirstQuestion && (
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            width: '100%',
            marginTop: '8px',
            paddingLeft: '20px',
            paddingRight: '20px'
          }}>
            <span style={{
              fontFamily: 'IBM Plex Sans',
              fontSize: '11px',
              color: '#757575',
              fontWeight: 400
            }}>
              Not at all likely
            </span>
            <span style={{
              fontFamily: 'IBM Plex Sans',
              fontSize: '11px',
              color: '#757575',
              fontWeight: 400
            }}>
              Extremely likely
            </span>
          </div>
        )}
      </div>
      {tooltip.visible && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x + 10,
            top: tooltip.y + 10,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            color: '#fff',
            padding: '6px 10px',
            borderRadius: '4px',
            fontSize: '12px',
            fontFamily: "'IBM Plex Sans', sans-serif",
            zIndex: 10000,
            pointerEvents: 'none',
            maxWidth: '350px',
            wordWrap: 'break-word',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            lineHeight: '1.4'
          }}
        >
          {tooltip.text}
        </div>
      )}
    </Item>
  );
}
